import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import { useQuery } from '@apollo/client';
import debug from 'debug';
import { GET_APP_CONFIG } from '../gql/queries/appConfig';
import { useAuth } from './useAuth';

const log = debug('rois:hooks:appconfig');

if (import.meta.env.DEV) {
  debug.enable('rois:hooks:appconfig*');
}

interface AppConfigType {
  id: string;
  availableConcessionIds: string[];
  tenantId: string;
  createdAt: string;
  updatedAt: string;
}

export interface AppConfigContextType {
  appConfig: AppConfigType | null;
  availableConcessionIds: string[];
  loading: boolean;
  error: any;
  refetch: () => void;
  clearAppConfig: () => void;
}

const AppConfigContext = createContext<AppConfigContextType | undefined>(
  undefined
);

export const AppConfigProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [appConfig, setAppConfig] = useState<AppConfigType | null>(null);
  const [hasCheckedCache, setHasCheckedCache] = useState(false);

  // Cargar desde sessionStorage al montar (PRIMERO, antes del query)
  useEffect(() => {
    const cachedConfig = sessionStorage.getItem('appConfig');
    if (cachedConfig && !appConfig) {
      try {
        const parsed = JSON.parse(cachedConfig);
        setAppConfig(parsed);
        log('✅ Cargado desde cache:', parsed);
      } catch (e) {
        console.error('❌ [APPCONFIG] Error parsing cached appConfig:', e);
      }
    }
    setHasCheckedCache(true);
  }, [appConfig]);

  // Solo hacer query si:
  // 1. El usuario está autenticado
  // 2. Ya revisamos el cache
  // 3. NO hay appConfig en cache (para evitar llamadas innecesarias)
  const shouldSkipQuery = !user?.accessToken || !hasCheckedCache || !!appConfig;

  const { data, loading, error, refetch } = useQuery(GET_APP_CONFIG, {
    skip: shouldSkipQuery,
    fetchPolicy: 'network-only', // Siempre obtener datos frescos cuando se ejecuta
    onCompleted: (data) => {
      if (data?.appConfig) {
        setAppConfig(data.appConfig);
        // Guardar en sessionStorage para persistencia
        sessionStorage.setItem('appConfig', JSON.stringify(data.appConfig));
        log('✅ Cargado desde servidor:', data.appConfig);
      }
    },
  });

  // Función para limpiar appConfig (llamada explícitamente desde logout)
  const clearAppConfig = () => {
    setAppConfig(null);
    sessionStorage.removeItem('appConfig');
    log('🗑️ Cache limpiado (logout)');
  };

  const value = {
    appConfig: appConfig || data?.appConfig || null,
    availableConcessionIds:
      appConfig?.availableConcessionIds ||
      data?.appConfig?.availableConcessionIds ||
      [],
    loading,
    error,
    refetch,
    clearAppConfig,
  };

  return (
    <AppConfigContext.Provider value={value}>
      {children}
    </AppConfigContext.Provider>
  );
};

export const useAppConfig = () => {
  const context = useContext(AppConfigContext);
  if (context === undefined) {
    throw new Error('useAppConfig must be used within AppConfigProvider');
  }
  return context;
};
